import { rt } from "../i18n/runtime";
import { apiRequest } from "./apiClient";
import { createActivity, getActivityActor } from "./activityService";
import { bagliHatirlaticilariSil } from "./linkedReminderService";
import { emitRestDataChanged } from "./restEventBus";
import { isDataSyncConnected } from "./dataSyncService";
import { readSensitiveItem, writeSensitiveItem } from "../utils/sensitiveSessionStorage";

const enc = (v) => encodeURIComponent(String(v || ""));

export const savingsCollectionName        = "financeSavings";
export const installmentsCollectionName   = "homeInstallments";
export const subscriptionsCollectionName  = "financeSubscriptions";
export const billsCollectionName          = "financeBills";

const typeMap = {
  saving: "saving",
  installment: "installment",
  subscription: "subscription",
  bill: "bill",
};

const financeItemsMemoryCache = new Map();
const financeCacheKey = (familyId, type) => `familyPlanner.financeToolsCache.${familyId}.${type}`;

if (typeof window !== "undefined") {
  window.addEventListener("fp:userScopedStorageCleared", () => {
    financeItemsMemoryCache.clear();
  });
}

export const getCachedFinanceToolItems = ({ familyId, type }) => {
  if (!familyId || !type) return [];
  const key = `${familyId}:${type}`;
  const memoryItems = financeItemsMemoryCache.get(key);
  if (Array.isArray(memoryItems)) return [...memoryItems];

  try {
    const raw = readSensitiveItem(financeCacheKey(familyId, type));
    const parsed = raw ? JSON.parse(raw) : [];
    if (Array.isArray(parsed)) {
      financeItemsMemoryCache.set(key, parsed);
      return [...parsed];
    }
  } catch (_) {}

  return [];
};

const setCachedFinanceToolItems = ({ familyId, type, items }) => {
  if (!familyId || !type) return;
  const safeItems = Array.isArray(items) ? items : [];
  const key = `${familyId}:${type}`;
  financeItemsMemoryCache.set(key, safeItems);
  try {
    writeSensitiveItem(financeCacheKey(familyId, type), JSON.stringify(safeItems));
  } catch (_) {}
};

const getFinanceItems = async (familyId, type) => {
  if (!familyId) return [];
  const data = await apiRequest(`/finance/${enc(familyId)}?type=${enc(type)}`);
  return Array.isArray(data?.items) ? data.items : [];
};

const financeListenerChannels = new Map();
const FINANCE_LISTENER_GRACE_MS = 5000;

const getFinanceListenerChannelKey = (familyId, type) => `${familyId}:${type}`;

const createFinanceListenerChannel = ({ familyId, type }) => {
  const key = getFinanceListenerChannelKey(familyId, type);
  const channel = {
    familyId,
    type,
    subscribers: new Set(),
    currentItems: getCachedFinanceToolItems({ familyId, type }),
    timer: null,
    cleanupTimer: null,
    inFlight: null,
    queuedReload: false,
    destroyed: false,
    onChanged: null,
  };

  const publish = (items) => {
    if (channel.destroyed) return;
    channel.currentItems = Array.isArray(items) ? items : [];
    setCachedFinanceToolItems({ familyId, type, items: channel.currentItems });
    channel.subscribers.forEach((subscriber) => {
      try {
        subscriber.onData([...channel.currentItems]);
      } catch (_) {}
    });
  };

  const publishError = (error) => {
    channel.subscribers.forEach((subscriber) => {
      try {
        subscriber.onError && subscriber.onError(error);
      } catch (_) {}
    });
  };

  const load = async () => {
    if (channel.destroyed) return;
    if (channel.inFlight) {
      channel.queuedReload = true;
      return channel.inFlight;
    }

    channel.inFlight = (async () => {
      try {
        const items = await getFinanceItems(familyId, type);
        if (!channel.destroyed) publish(items);
      } catch (error) {
        if (!channel.destroyed) publishError(error);
      } finally {
        channel.inFlight = null;
        if (channel.queuedReload && !channel.destroyed) {
          channel.queuedReload = false;
          window.setTimeout(load, 0);
        }
      }
    })();

    return channel.inFlight;
  };

  const upsertLocal = (item) => {
    if (!item?.id) return;
    const exists = channel.currentItems.some((x) => x.id === item.id);
    publish(exists
      ? channel.currentItems.map((x) => (x.id === item.id ? { ...x, ...item } : x))
      : [{ ...item }, ...channel.currentItems]);
  };

  const deleteLocal = (itemId) => {
    if (!itemId) return;
    publish(channel.currentItems.filter((x) => x.id !== itemId));
  };

  channel.onChanged = (event) => {
    const d = event?.detail || {};
    if (d.familyId !== familyId || d.resource !== "finance") return;

    const itemType = d.item?.itemType || d.item?.item_type || d.extra?.itemType;
    if (itemType && itemType !== type) return;

    if (d.action === "added" || d.action === "updated") upsertLocal(d.item);
    if (d.action === "deleted") deleteLocal(d.itemId);

    // Tam item payload'ı yeni ve eski backend cevaplarında normalize edilmiş
    // finans öğesidir. Eksik/refresh olayı gelirse doğruluk için snapshot korunur.
    if (!d.item && !d.itemId && !d.extra?.linkedPayment) window.setTimeout(load, 250);
  };

  channel.load = load;
  channel.destroy = () => {
    if (channel.destroyed) return;
    channel.destroyed = true;
    if (channel.timer) window.clearInterval(channel.timer);
    if (channel.cleanupTimer) window.clearTimeout(channel.cleanupTimer);
    window.removeEventListener("fp:restDataChanged", channel.onChanged);
    window.removeEventListener("fp:dataChanged", channel.onChanged);
    financeListenerChannels.delete(key);
  };

  window.addEventListener("fp:restDataChanged", channel.onChanged);
  window.addEventListener("fp:dataChanged", channel.onChanged);
  channel.timer = window.setInterval(() => {
    if (!isDataSyncConnected(familyId)) load();
  }, 20000);
  financeListenerChannels.set(key, channel);
  load();
  return channel;
};

const makeListener = (type) => ({ familyId, onData, onError }) => {
  if (!familyId) return () => {};

  const key = getFinanceListenerChannelKey(familyId, type);
  let channel = financeListenerChannels.get(key);
  if (!channel || channel.destroyed) {
    channel = createFinanceListenerChannel({ familyId, type });
  }

  if (channel.cleanupTimer) {
    window.clearTimeout(channel.cleanupTimer);
    channel.cleanupTimer = null;
  }

  const subscriber = { onData, onError };
  channel.subscribers.add(subscriber);

  if (channel.currentItems.length > 0) {
    window.setTimeout(() => {
      if (channel.subscribers.has(subscriber)) {
        onData([...channel.currentItems]);
      }
    }, 0);
  }

  return () => {
    channel.subscribers.delete(subscriber);
    if (channel.subscribers.size > 0 || channel.destroyed) return;

    // Finans sayfasındaki kısa mount/unmount geçişlerinde listener'ı hemen kapatmayarak
    // aynı verilerin birkaç saniye içinde yeniden indirilmesini önler.
    channel.cleanupTimer = window.setTimeout(() => {
      if (channel.subscribers.size === 0) channel.destroy();
    }, FINANCE_LISTENER_GRACE_MS);
  };
};

export const listenFinanceSavings = makeListener(typeMap.saving);

const globalAssetPriceChannel = {
  subscribers: new Set(),
  currentData: null,
  timer: null,
  cleanupTimer: null,
  inFlight: null,
};

const loadGlobalAssetPrices = async () => {
  if (globalAssetPriceChannel.inFlight) return globalAssetPriceChannel.inFlight;

  globalAssetPriceChannel.inFlight = (async () => {
    try {
      const data = await apiRequest(`/finance/market-prices`);
      globalAssetPriceChannel.currentData = {
        prices: data?.prices || {},
        updatedAt: data?.updatedAt || null,
        sources: data?.sources || {},
        currencyPolicy: data?.currencyPolicy || {},
      };
      globalAssetPriceChannel.subscribers.forEach((subscriber) => {
        try { subscriber.onData && subscriber.onData(globalAssetPriceChannel.currentData); } catch (_) {}
      });
    } catch (error) {
      globalAssetPriceChannel.subscribers.forEach((subscriber) => {
        try { subscriber.onError && subscriber.onError(error); } catch (_) {}
      });
      if (!globalAssetPriceChannel.currentData) {
        globalAssetPriceChannel.currentData = { prices: {}, updatedAt: null, sources: {} };
        globalAssetPriceChannel.subscribers.forEach((subscriber) => {
          try { subscriber.onData && subscriber.onData(globalAssetPriceChannel.currentData); } catch (_) {}
        });
      }
    } finally {
      globalAssetPriceChannel.inFlight = null;
    }
  })();

  return globalAssetPriceChannel.inFlight;
};

export const getGlobalAssetPricesOnce = async () => {
  if (globalAssetPriceChannel.currentData) return globalAssetPriceChannel.currentData;
  await loadGlobalAssetPrices();
  return globalAssetPriceChannel.currentData || { prices: {}, updatedAt: null, sources: {} };
};

export const listenGlobalAssetPrices = ({ onData, onError } = {}) => {
  if (globalAssetPriceChannel.cleanupTimer) {
    window.clearTimeout(globalAssetPriceChannel.cleanupTimer);
    globalAssetPriceChannel.cleanupTimer = null;
  }

  const subscriber = { onData, onError };
  globalAssetPriceChannel.subscribers.add(subscriber);

  if (globalAssetPriceChannel.currentData) {
    window.setTimeout(() => {
      if (globalAssetPriceChannel.subscribers.has(subscriber)) {
        onData && onData(globalAssetPriceChannel.currentData);
      }
    }, 0);
  }

  if (!globalAssetPriceChannel.timer) {
    loadGlobalAssetPrices();
    globalAssetPriceChannel.timer = window.setInterval(loadGlobalAssetPrices, 5 * 60 * 1000);
  }

  return () => {
    globalAssetPriceChannel.subscribers.delete(subscriber);
    if (globalAssetPriceChannel.subscribers.size > 0) return;

    globalAssetPriceChannel.cleanupTimer = window.setTimeout(() => {
      if (globalAssetPriceChannel.subscribers.size > 0) return;
      if (globalAssetPriceChannel.timer) window.clearInterval(globalAssetPriceChannel.timer);
      globalAssetPriceChannel.timer = null;
      globalAssetPriceChannel.cleanupTimer = null;
    }, FINANCE_LISTENER_GRACE_MS);
  };
};

export const listenHomeInstallments = makeListener(typeMap.installment);

const postFinanceTool = async ({ familyId, itemType, data }) => {
  const response = await apiRequest(`/finance/${enc(familyId)}`, {
    method: "POST",
    body: JSON.stringify({ ...data, itemType }),
  });
  const item = response?.item || { ...data, itemType };
  emitRestDataChanged({ familyId, resource: "finance", action: "added", item });
  (response?.relatedChanges || []).forEach((change) => {
    emitRestDataChanged({
      familyId,
      resource: "finance",
      action: change.action,
      item: change.item || null,
      itemId: change.itemId || null,
      extra: { silentNotification: true, linkedPayment: true },
    });
  });
  return item;
};

const patchFinanceTool = async ({ familyId, itemId, patch }) => {
  const response = await apiRequest(`/finance/${enc(familyId)}/${enc(itemId)}`, {
    method: "PATCH",
    body: JSON.stringify(patch || {}),
  });
  const item = response?.item || { ...(patch || {}), id: itemId };
  emitRestDataChanged({ familyId, resource: "finance", action: "updated", item });
  (response?.relatedChanges || []).forEach((change) => {
    emitRestDataChanged({
      familyId,
      resource: "finance",
      action: change.action,
      item: change.item || null,
      itemId: change.itemId || null,
      extra: { silentNotification: true, linkedPayment: true },
    });
  });
  return item;
};

const deleteFinanceTool = async ({ familyId, itemId, itemType }) => {
  await apiRequest(`/finance/${enc(familyId)}/${enc(itemId)}`, { method: "DELETE" });
  emitRestDataChanged({ familyId, resource: "finance", action: "deleted", itemId, extra: { itemType } });
};

export const addFinanceSaving = async ({ familyId, saving, user, profilAvatar = null, profilAd = "" }) => {
  if (!familyId) throw new Error(rt("service.error.familyIdMissing"));
  const item = await postFinanceTool({
    familyId,
    itemType: "saving",
    data: {
      ...saving,
      createdByUid: user?.uid || "",
      ekleyenUid: user?.uid || "",
      createdByName: profilAd || user?.displayName || "",
      ekleyen: profilAd || user?.displayName || "",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  });
  await createActivity({
    familyId,
    type: "finance_added",
    category: "finance",
    actor: getActivityActor({ user, name: profilAd || user?.displayName || "", avatar: profilAvatar }),
    payload: { title: saving?.ad || saving?.name || saving?.assetName || saving?.assetKey || rt("finance.tab.savings"), amount: saving?.tutar || saving?.miktar || "" },
    targetPage: "finans",
    targetId: item.id,
  });
  return { id: item.id, ...item };
};

export const updateFinanceSaving = async ({ familyId, savingId, patch }) => {
  if (!familyId || !savingId) throw new Error(rt("service.error.savingNotFound"));
  return patchFinanceTool({ familyId, itemId: savingId, patch: { ...patch, updatedAt: new Date().toISOString() } });
};

export const updateFinanceSavingsCurrentPriceByAsset = async ({ familyId, assetKey, guncelFiyat }) => {
  if (!familyId || !assetKey) throw new Error(rt("service.error.savingNotFound"));
  const response = await apiRequest(`/finance/${enc(familyId)}/savings/asset-price`, {
    method: "PATCH",
    body: JSON.stringify({ assetKey, guncelFiyat: Number(guncelFiyat) }),
  });
  (response?.items || []).forEach((item) => emitRestDataChanged({ familyId, resource: "finance", action: "updated", item }));
  return response;
};

export const deleteFinanceSaving = async ({ familyId, savingId, saving = null }) => {
  if (!familyId || !savingId) throw new Error(rt("service.error.savingNotFound"));
  await bagliHatirlaticilariSil({ familyId, kaynak: "financeSaving", kaynakId: savingId, reminderId: saving?.reminderId, reminderIds: saving?.reminderIds });
  return deleteFinanceTool({ familyId, itemId: savingId, itemType: "saving" });
};

export const addHomeInstallment = async ({ familyId, installment, user, profilAvatar = null, profilAd = "" }) => {
  if (!familyId) throw new Error(rt("service.error.familyIdMissing"));
  const item = await postFinanceTool({
    familyId,
    itemType: "installment",
    data: { ...installment, createdByUid: user?.uid || "", ekleyenUid: user?.uid || "", createdByName: profilAd || user?.displayName || "", ekleyen: profilAd || user?.displayName || "", createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  });
  await createActivity({
    familyId,
    type: "finance_added",
    category: "finance",
    actor: getActivityActor({ user, name: profilAd || user?.displayName || "", avatar: profilAvatar }),
    payload: { title: installment?.ad || installment?.title || installment?.baslik || installment?.name || rt("finance.tab.installments"), amount: installment?.amount || installment?.tutar || "" },
    targetPage: "finans",
    targetId: item.id,
  });
  return { id: item.id, ...item };
};

export const updateHomeInstallment = async ({ familyId, installmentId, patch }) => {
  if (!familyId || !installmentId) throw new Error(rt("service.error.installmentNotFound"));
  return patchFinanceTool({ familyId, itemId: installmentId, patch: { ...patch, updatedAt: new Date().toISOString() } });
};

export const addFinanceDebtPayment = async ({ familyId, installmentId, payment }) => {
  if (!familyId || !installmentId) throw new Error(rt("service.error.installmentNotFound"));
  const response = await apiRequest(`/finance/${enc(familyId)}/${enc(installmentId)}/payments`, {
    method: "POST",
    body: JSON.stringify(payment || {}),
  });
  if (response?.item) {
    emitRestDataChanged({ familyId, resource: "finance", action: "updated", item: response.item });
  }
  (response?.relatedChanges || []).forEach((change) => {
    emitRestDataChanged({
      familyId,
      resource: "finance",
      action: change.action,
      item: change.item || null,
      itemId: change.itemId || null,
      extra: { silentNotification: true, linkedPayment: true },
    });
  });
  return response;
};

export const deleteHomeInstallmentReminders = async ({ familyId, installmentId, installment = null }) => {
  if (!familyId || !installmentId) return;
  await bagliHatirlaticilariSil({
    familyId,
    kaynak: null,
    kaynakId: installmentId,
    reminderId: installment?.reminderId,
    reminderIds: installment?.reminderIds,
  });
};

export const deleteHomeInstallment = async ({ familyId, installmentId, installment = null }) => {
  if (!familyId || !installmentId) throw new Error(rt("service.error.installmentNotFound"));
  await bagliHatirlaticilariSil({ familyId, kaynak: "financeInstallment", kaynakId: installmentId, reminderId: installment?.reminderId, reminderIds: installment?.reminderIds });
  return deleteFinanceTool({ familyId, itemId: installmentId, itemType: "installment" });
};

export const listenFinanceSubscriptions = makeListener(typeMap.subscription);

export const addFinanceSubscription = async ({ familyId, subscription, user, profilAvatar = null, profilAd = "" }) => {
  if (!familyId) throw new Error(rt("service.error.familyIdMissing"));
  const item = await postFinanceTool({
    familyId,
    itemType: "subscription",
    data: { ...subscription, createdByUid: user?.uid || "", ekleyenUid: user?.uid || "", createdByName: profilAd || user?.displayName || "", ekleyen: profilAd || user?.displayName || "", createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  });
  await createActivity({
    familyId,
    type: "finance_added",
    category: "finance",
    actor: getActivityActor({ user, name: profilAd || user?.displayName || "", avatar: profilAvatar }),
    payload: { title: subscription?.ad || subscription?.title || subscription?.name || subscription?.service || rt("finance.tab.subscriptions"), amount: subscription?.amount || subscription?.tutar || "" },
    targetPage: "finans",
    targetId: item.id,
  });
  return { id: item.id, ...item };
};

export const updateFinanceSubscription = async ({ familyId, subscriptionId, patch }) => {
  if (!familyId || !subscriptionId) throw new Error(rt("service.error.subscriptionNotFound"));
  return patchFinanceTool({ familyId, itemId: subscriptionId, patch: { ...patch, updatedAt: new Date().toISOString() } });
};

export const deleteFinanceSubscription = async ({ familyId, subscriptionId, subscription = null }) => {
  if (!familyId || !subscriptionId) throw new Error(rt("service.error.subscriptionNotFound"));
  await bagliHatirlaticilariSil({ familyId, kaynak: "financeSubscription", kaynakId: subscriptionId, reminderId: subscription?.reminderId, reminderIds: subscription?.reminderIds });
  return deleteFinanceTool({ familyId, itemId: subscriptionId, itemType: "subscription" });
};

export const listenFinanceBills = makeListener(typeMap.bill);

export const addFinanceBill = async ({ familyId, bill, user, profilAvatar = null, profilAd = "" }) => {
  if (!familyId) throw new Error(rt("service.error.familyIdMissing"));
  const item = await postFinanceTool({
    familyId,
    itemType: "bill",
    data: { ...bill, createdByUid: user?.uid || "", ekleyenUid: user?.uid || "", createdByName: profilAd || user?.displayName || "", ekleyen: profilAd || user?.displayName || "", createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
  });
  await createActivity({
    familyId,
    type: "finance_added",
    category: "finance",
    actor: getActivityActor({ user, name: profilAd || user?.displayName || "", avatar: profilAvatar }),
    payload: { title: bill?.ad || bill?.title || bill?.name || bill?.billType || bill?.kategori || rt("finance.tab.bills"), amount: bill?.amount || bill?.tutar || "" },
    targetPage: "finans",
    targetId: item.id,
  });
  return { id: item.id, ...item };
};

export const updateFinanceBill = async ({ familyId, billId, patch }) => {
  if (!familyId || !billId) throw new Error(rt("service.error.billNotFound"));
  return patchFinanceTool({ familyId, itemId: billId, patch: { ...patch, updatedAt: new Date().toISOString() } });
};

export const deleteFinanceBill = async ({ familyId, billId, bill = null }) => {
  if (!familyId || !billId) throw new Error(rt("service.error.billNotFound"));
  await bagliHatirlaticilariSil({ familyId, kaynak: "financeBill", kaynakId: billId, reminderId: bill?.reminderId, reminderIds: bill?.reminderIds });
  return deleteFinanceTool({ familyId, itemId: billId, itemType: "bill" });
};
